import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';
import { mapApiKnowledgeDocument } from './knowledgeBase';
import type { KbDocument } from './knowledgeBase';

// ── Knowledge Base doc management ─────────────────────────────────────

/** `POST /api/knowledge/{doc_id}/submit` — submit document for approval. */
export async function submitKnowledgeDocument(docId: string, axiosInstance?: AxiosInstance): Promise<KbDocument> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<any>(`${apiConfig.voiceEndpoints.knowledge}/${docId}/submit`, {});
  return mapApiKnowledgeDocument(response.data);
}

/** `POST /api/knowledge/{doc_id}/approve` — approve and push to ElevenLabs. */
export async function approveKnowledgeDocument(docId: string, axiosInstance?: AxiosInstance): Promise<KbDocument> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<any>(`${apiConfig.voiceEndpoints.knowledge}/${docId}/approve`, {});
  return mapApiKnowledgeDocument(response.data);
}

/** `POST /api/knowledge/{doc_id}/reject` — reject pending document. */
export async function rejectKnowledgeDocument(docId: string, reason?: string, axiosInstance?: AxiosInstance): Promise<KbDocument> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<any>(`${apiConfig.voiceEndpoints.knowledge}/${docId}/reject`, { reason });
  return mapApiKnowledgeDocument(response.data);
}

/** `POST /api/knowledge/{doc_id}/rollback` — rollback to previous version. */
export async function rollbackKnowledgeDocument(docId: string, axiosInstance?: AxiosInstance): Promise<KbDocument> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<any>(`${apiConfig.voiceEndpoints.knowledge}/${docId}/rollback`, {});
  return mapApiKnowledgeDocument(response.data);
}

/** `DELETE /api/knowledge/{doc_id}` — soft delete knowledge document. */
export async function deleteKnowledgeDocument(docId: string, axiosInstance?: AxiosInstance): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.delete(`${apiConfig.voiceEndpoints.knowledge}/${docId}`);
  return response.status === 200 || response.status === 204;
}

/** `POST /api/knowledge/{doc_id}/categories` — set document categories. */
export async function setKnowledgeDocumentCategories(docId: string, categories: string[], axiosInstance?: AxiosInstance): Promise<KbDocument> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<any>(`${apiConfig.voiceEndpoints.knowledge}/${docId}/categories`, { categories });
  return mapApiKnowledgeDocument(response.data);
}

/** `POST /api/knowledge/{doc_id}/agents` — set document agents. */
export async function setKnowledgeDocumentAgents(docId: string, agentIds: string[], axiosInstance?: AxiosInstance): Promise<KbDocument> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<any>(`${apiConfig.voiceEndpoints.knowledge}/${docId}/agents`, { agent_ids: agentIds });
  return mapApiKnowledgeDocument(response.data);
}

/** `GET /api/knowledge/{doc_id}/content` — get plain text content of a doc. */
export async function fetchKnowledgeDocumentContent(docId: string, axiosInstance?: AxiosInstance): Promise<string> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(`${apiConfig.voiceEndpoints.knowledge}/${docId}/content`);
  return String(response.data?.content ?? response.data ?? '');
}

/** `GET /api/knowledge/{doc_id}/download` — download doc as .txt file. */
export async function downloadKnowledgeDocument(docId: string, axiosInstance?: AxiosInstance): Promise<Blob> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<Blob>(`${apiConfig.voiceEndpoints.knowledge}/${docId}/download`, { responseType: 'blob' });
  return response.data;
}

/** `GET /api/knowledge/{campaign_id}` — list knowledge documents for a campaign. */
export async function fetchCampaignKnowledge(campaignId: string, axiosInstance?: AxiosInstance): Promise<KbDocument[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(`${apiConfig.voiceEndpoints.knowledge}/${campaignId}`);
  const list = Array.isArray(response.data) ? response.data : response.data?.documents ?? [];
  return list.map(mapApiKnowledgeDocument);
}
